import get from 'lodash/get';
import getOr from 'lodash/fp/getOr';
import compact from 'lodash/compact';
import first from 'lodash/first';
import truncate from 'lodash/truncate';
import Joi from 'joi-full';
import { USER_TYPES } from '../../auth/constants';
import { SHOP_TYPE } from '../../main/shops/constants';
import { getShopBrand, isBarberUser, isBrandViewActive } from '../../auth/selectors';

export const canUserModifyOptions = (user) =>
  user.kind === USER_TYPES.admin ||
  user.kind === USER_TYPES.regularAdmin ||
  user.kind === USER_TYPES.agent ||
  user.kind === USER_TYPES.shop ||
  get(user, 'barbers[0].shop.kind') === SHOP_TYPE.rental;

export const canUserViewCustomersReport = (user, shopKind) =>
  user.kind === USER_TYPES.shop || (user.kind === USER_TYPES.barber && shopKind === SHOP_TYPE.rental);

export const canUserSetWaitingList = canUserViewCustomersReport;

export const canUseEngage = getOr(false, 'auth.shopBrand.campaignsEnabled');
export const canUseConversionTracking = getOr(false, 'auth.shopBrand.conversionTracking');
export const canUseProductReporting = getOr(false, 'auth.shopBrand.productReportingEnabled');
export const canUseCommissionReporting = getOr(false, 'auth.shopBrand.commissionReportingEnabled');
export const canUseChat = getOr(false, 'auth.shopBrand.chatEnabled');
export const canUseEcommerce = (state) => get(state, 'auth.shopBrand.settings.ecommerceSyncIntegration') || false;
export const canUseMailchimp = (state) => get(state, 'auth.shopBrand.settings.mailchimpIntegrationEnabled') || false;
export const canUseIntegration = (state) => canUseEcommerce(state) || canUseMailchimp(state);

// register permissions
export const canUserViewRegister = (permissions) => get(permissions, 'register.view');
export const canUserViewCashRegister = (permissions, isRentalBarber) =>
  get(permissions, 'cashRegister.view') || isRentalBarber;
export const canUserOpenCashDrawerManually = (permissions, isRentalBarber) =>
  canUserViewCashRegister(permissions, isRentalBarber) && get(permissions, 'cashRegister.modify');

export const canUserViewCustomers = (permissions) => get(permissions, 'clients.view');
export const canUserAddCustomers = (permissions) => get(permissions, 'clients.modify');
export const canUserModifyCustomers = (permissions) => get(permissions, 'clients.modify');
export const canUserViewServices = (permissions) => get(permissions, 'services.view');
export const canUserModifyServices = (permissions) => get(permissions, 'services.modify');
export const canUserAddServices = (permissions) => get(permissions, 'services.modify');
export const canUserViewProfile = (permissions) => get(permissions, 'profile.view');
export const canUserModifyProfile = (permissions) => get(permissions, 'profile.modify');
export const canUserViewSchedules = (permissions) => get(permissions, 'workHours.view');
export const canUserModifySchedules = (permissions) => get(permissions, 'workHours.modify');
export const canUserViewBankAccounts = (permissions) => get(permissions, 'bankAccount.view');
export const canUserModifyBankAccounts = (permissions) => get(permissions, 'bankAccount.modify');
export const canUserAddBankAccounts = (permissions) => get(permissions, 'bankAccount.modify');
export const canUserViewReports = (permissions) => get(permissions, 'reports.view');
export const canUserViewAppointments = (permissions) => get(permissions, 'appointments.view');
export const canUserViewAppointmentContactInfo = (permissions) => get(permissions, 'appointments.show_contact_info');
export const canUserModifyAppointments = (permissions) => get(permissions, 'appointments.modify');
export const canUserCancelAppointments = (permissions) =>
  get(permissions, 'appointments.cancel') && canUserViewRegister(permissions);
export const canUserBookAppointments = (permissions) => get(permissions, 'appointments.book');
export const canUserViewAppointmentHistory = (permissions) => get(permissions, 'appointments.history');
export const canUserUseBlockTime = (permissions) =>
  get(permissions, 'appointments.blockTime') || get(permissions, 'blockTime.modify');
export const canUserViewShopDetails = (permissions) => get(permissions, 'settings.view');
export const canUserModifyShopDetails = (permissions) => get(permissions, 'settings.modify');
export const canUserViewBarberSettings = (permissions) => get(permissions, 'barberSettings.view');
export const canUserModifyBarberSettings = (permissions) => get(permissions, 'barberSettings.modify');
export const canUserViewBarbers = (permissions) => get(permissions, 'barbers.view');
export const canUserModifyBarbers = (permissions) => get(permissions, 'barbers.modify');
export const canUserViewTimeReport = (permissions) => get(permissions, 'timeTrack.view');
export const canUserViewDashboard = (permissions) => get(permissions, 'dashboard.view');
export const canUserViewAnalytics = (permissions) => get(permissions, 'analytics.view');
export const canUserViewEngage = (permissions) => get(permissions, 'engage.view');
export const canUserViewProducts = (permissions) => get(permissions, 'products.view');
export const canUserModifyProducts = (permissions) => get(permissions, 'products.modify');
export const canUserModifyInventory = (permissions) => get(permissions, 'inventory.modify');
export const canUserViewTaxes = (permissions) => get(permissions, 'taxes.view');
export const canUserModifyTaxes = (permissions) => get(permissions, 'taxes.modify');
export const canUserViewDiscounts = (permissions) => get(permissions, 'discounts.view');
export const canUserModifyDiscounts = (permissions) => get(permissions, 'discounts.modify');
export const canUserViewPromos = (permissions) => get(permissions, 'promos.view');
export const canUserModifyPromos = (permissions) => get(permissions, 'promos.modify');
export const canUserViewWaitingList = (permissions) => get(permissions, 'waitingList.view');
export const canUserModifyWaitingList = (permissions) => get(permissions, 'waitingList.modify');
export const canUserUseListeningMode = (permissions) => !get(permissions, 'kiosk.view');
export const canUserUseHandoff = (permissions) => get(permissions, 'kiosk.view');

export const canUserViewPayoutReport = (user, shop) => {
  if (!shop || shop.kind === SHOP_TYPE.rental) {
    return false;
  }

  if (user.kind === USER_TYPES.shop) {
    return true;
  }

  return user.kind === USER_TYPES.barber;
};

export const canUserViewDailySummary = (user, shop, permissions) => {
  if (user.kind !== USER_TYPES.barber) {
    return get(permissions, 'dailySummary.view');
  }

  return shop.kind === SHOP_TYPE.rental && canUserViewRegister(permissions);
};

export const canUserViewDailySummaryHistory = (user, permissions, shop = null) => {
  if (user.kind !== USER_TYPES.barber) {
    return get(permissions, 'dailySummaryHistory.view');
  }

  return shop && shop.kind === SHOP_TYPE.rental && canUserViewRegister(permissions);
};

export const fullName = (struct, short = false) => {
  if (!struct) return '';

  const { firstName, lastName } = struct;
  const name = compact([firstName, short ? first(lastName) : lastName]).join(' ');
  return short
    ? truncate(name, {
        length: 35,
        separator: ' ',
      })
    : name;
};

export const isSignedUpCustomer = (customer) =>
  customer.user && !!Joi.validate(customer.user.username, Joi.string().guid()).error;

export const canUseChairRental = (shop, hybridExposeRentCollect) =>
  (shop.kind === SHOP_TYPE.rental || hybridExposeRentCollect) && shop.boothRentalEnabled;

export const isShopUser = (user) => user.kind === USER_TYPES.shop;
export const isBrandUser = (user) => user.kind === USER_TYPES.brand;

export const canUseCustomReports = (state) => {
  const brand = get(state, 'auth.brand') || getShopBrand(state);
  return getOr(false, 'customReportingEnabled', brand);
};

export const canUserViewCustomReports = (state) => {
  const user = get(state, 'auth.user');

  return canUseCustomReports(state) && (isShopUser(user) || isBrandUser(user));
};
export const canUserEditCustomReports = (state) => canUseCustomReports(state) && isBrandViewActive(state);

export const isBrandOrSingleShopBrand = (state) => {
  const user = get(state, 'auth.user');
  const shopsCount = get(state, 'auth.shopBrand.shopCount');
  const isShopUserOfSingleShopBrand = isShopUser(user) && shopsCount === 1;

  return isBrandViewActive(state) || isShopUserOfSingleShopBrand;
};

export const canUserViewCustomCharge = (permissions) => get(permissions, 'customCharge.view');

export const notBarberAndCanModifyProducts = (state) =>
  canUserModifyProducts(state.auth.permissions) && !isBarberUser(state);

export const canUserViewStaffManagement = (state) => {
  const hasBrandStaffManagementEnabled = getOr(false, 'settings.staffManagementEnabled', getShopBrand(state));
  const hasUserViewStaffManagementPermission = getOr(false, 'auth.permissions.staffManagement.view', state);

  return hasBrandStaffManagementEnabled && hasUserViewStaffManagementPermission;
};

export const canUserModifyStaffManagement = (permissions) => get(permissions, 'staffManagement.modify');

export const canUserViewAutoPayoutSettings = (permissions) => get(permissions, 'autoPayout.view');
export const canUserModifyAutoPayoutSettings = (permissions) => get(permissions, 'autoPayout.modify');
